import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { THREE_PL_EXCEL_REPORTS, type ThreePlReportDefinition } from '../../models/reports';
import { useThreePlExcelExport } from '../../hooks/useThreePlExcelExport';
import { ExportCompletedPanel } from '../display/ExportCompletedPanel';
import { ExportProgressPanel } from '../display/ExportProgressPanel';
import { ReportsList } from '../display/ReportsList';
import {
  GenerateReportDialog,
  type DateRangeValue,
  type InvoiceSelectionValue,
} from '../interaction/GenerateReportDialog';

const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultRange = (): DateRangeValue => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return {
    dateFrom: formatDateLocal(start),
    dateTo: formatDateLocal(end),
  };
};

export const Main = () => {
  const { t } = useTranslation('threePlExcelReportsModule');
  const {
    runExport,
    cancelExport,
    dismissCompletedExport,
    downloadExport,
    isExporting,
    isReadyToDownload,
    exportingReportId,
    activeExport,
  } = useThreePlExcelExport();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ThreePlReportDefinition | null>(null);
  const handleGenerateClick = useCallback(
    (report: ThreePlReportDefinition) => {
      if (report.paramType === 'none') {
        void runExport(report.id, {});
        return;
      }

      setSelectedReport(report);
      setDialogOpen(true);
    },
    [runExport],
  );

  const handleDateRangeSubmit = useCallback(
    async (range: DateRangeValue) => {
      if (!selectedReport || selectedReport.paramType !== 'date_range') return;
      setDialogOpen(false);
      await runExport(selectedReport.id, {
        date_from: range.dateFrom,
        date_to: range.dateTo,
      });
    },
    [runExport, selectedReport],
  );

  const handleInvoiceSubmit = useCallback(
    async (invoice: InvoiceSelectionValue) => {
      if (!selectedReport || selectedReport.paramType !== 'invoice_id') return;
      setDialogOpen(false);
      await runExport(selectedReport.id, {
        invoice_id: invoice.invoiceId,
        invoice_label: invoice.invoiceLabel,
      });
    },
    [runExport, selectedReport],
  );

  return (
    <div
      className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col px-6 pt-6 pb-10"
      data-testid="three-pl-excel-reports-view"
    >
      <div className="shrink-0">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('pageDescription')}</p>
      </div>

      {activeExport && (
        <div className="mt-6">
          {isReadyToDownload ? (
            <ExportCompletedPanel
              activeExport={activeExport}
              onDownload={downloadExport}
              onDismiss={dismissCompletedExport}
            />
          ) : (
            <ExportProgressPanel activeExport={activeExport} onCancel={cancelExport} />
          )}
        </div>
      )}

      <div className={activeExport ? 'mt-4' : 'mt-6'}>
        <ReportsList
          reports={THREE_PL_EXCEL_REPORTS}
          exportingReportId={exportingReportId}
          onGenerate={handleGenerateClick}
        />
      </div>

      <GenerateReportDialog
        report={selectedReport}
        open={dialogOpen}
        isExporting={isExporting}
        getDefaultRange={getDefaultRange}
        onOpenChange={setDialogOpen}
        onSubmitDateRange={handleDateRangeSubmit}
        onSubmitInvoice={handleInvoiceSubmit}
      />
    </div>
  );
};
